import { GridStack } from 'gridstack';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  Component,
  Input,
  AfterViewInit,
  EventEmitter,
  Output,
  ChangeDetectorRef,
  OnDestroy,
  HostListener,
  ChangeDetectionStrategy,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { SnAbilityService } from '@lib/ability';
import { SnWidget } from '@lib/api/entities';
import { SnOperationalViewsAPI } from '@lib/api/services';

import { AnalyticsWidgetComponent } from '../../widgets/analytics-widget/analytics-widget.component';
import { AddWidgetDialogComponent } from '../add-widget-dialog/add-widget-dialog.component';

@Component({
  selector: 'sn-op-view-widgets',
  templateUrl: './op-view-widgets.component.html',
  styleUrls: ['./op-view-widgets.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OperationalViewWidgetsPageComponent implements AfterViewInit, OnDestroy {
  @Input() public widgets: SnWidget[];
  @Input() public viewId: string;
  @Input() public isEditing: boolean;
  @Input() public isPinning: boolean;
  @Input() public isDeleting: boolean;
  @Output() public readonly widgetDeleted = new EventEmitter<SnWidget>();
  @Output() public readonly widgetEdited = new EventEmitter<void>();

  @ViewChild('analytics')
  public analyticsWidget: AnalyticsWidgetComponent;

  public readonly canDelete: boolean;

  public widthDom = window.innerWidth;
  public readonly canChange: boolean;
  private _grid: GridStack;

  private _destroy$ = new Subject();

  constructor(
    private readonly _cd: ChangeDetectorRef,
    private readonly _opViewAPI: SnOperationalViewsAPI,
    private readonly _ability: SnAbilityService,
    private readonly _dialog: MatDialog,
  ) {
    this.canDelete = this._ability.can({
      subject: 'dashboards/operationalview',
      action: 'delete',
    });
    this.canChange = this._ability.can({
      subject: 'dashboards/operationalview',
      action: 'change',
    });
  }

  @HostListener('window:resize', ['$event'])
  public onResize(event: any): void {
    this._resizeGrid(event.target.innerWidth);
  }

  public ngAfterViewInit(): void {
    this._grid = GridStack.init({
      disableDrag: !this.canChange ? true : null,
      disableResize: !this.canChange ? true : null,
      disableOneColumnMode: true,
      float: true,
      cellHeight: 100,
      cellHeightUnit: 'px',
      column: 8,
      resizable: {
        handles: 'e, se, s, sw, w',
      },
      draggable: {
        handle: '.drag-handle',
      },
    });

    this._resizeGrid(this.widthDom);
    this._grid.on('change', this._onWidgetChange.bind(this));
    this._getWidgetPinnedState();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    window.removeEventListener('resize', null);
  }

  public pinWidget(widget: SnWidget): void {
    const index = this.widgets.findIndex((w) => w.id === widget.id);
    if (index > -1) {
      this._grid.removeWidget(this._getWidgetElement(widget));
      this.widgets[index] = widget;
      setTimeout(() => {
        this._grid.makeWidget(this._getWidgetElement(widget));
        this._grid.resizable(this._getWidgetElement(widget), !widget.isPinned);
        this._grid.movable(this._getWidgetElement(widget), !widget.isPinned);
        this._grid.update(this._getWidgetElement(widget), {
          locked: widget.isPinned,
        });
      }, 500);
      this._cd.detectChanges();
    }
    this._cd.detectChanges();
  }

  public onDeleteWidget(widget: SnWidget): void {
    this.widgetDeleted.next(widget);
    this._cd.detectChanges();
  }

  public onWidgetUpdated(widget: SnWidget): void {
    this._opViewAPI
      .updateViewWidget(this.viewId, widget.id, widget)
      .pipe(takeUntil(this._destroy$))
      .subscribe();
  }

  public onPinWidget(widget: SnWidget): void {
    this._opViewAPI
      .updateViewWidget(this.viewId, widget.id, { isPinned: !widget.isPinned })
      .pipe(takeUntil(this._destroy$))
      .subscribe((w) => {
        this.pinWidget(w);
        this._cd.detectChanges();
      });
  }

  public edit(widget: SnWidget): void {
    const dialogRef = this._dialog.open(AddWidgetDialogComponent, {
      data: {
        viewId: this.viewId,
        widget,
      },
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((result) => {
        if (result) {
          this.widgetEdited.next();
        }
      });
  }

  private _getWidgetElement(widget: SnWidget): any {
    return document.querySelector(`#${widget.widget}__${widget.id}`);
  }

  private _resizeGrid(width?: number): void {
    this.widthDom = width || null;
    if (this.widthDom < 700) {
      this._grid.column(1, 'moveScale');
    } else if (this.widthDom > 1100) {
      this._grid.column(8, 'moveScale');
    }

    this._cd.detectChanges();
  }

  private _detectMob(): boolean {
    const toMatch = [
      /Android/i,
      /webOS/i,
      /iPhone/i,
      /iPad/i,
      /iPod/i,
      /BlackBerry/i,
      /Windows Phone/i,
    ];

    return toMatch.some((toMatchItem) => {
      return navigator.userAgent.match(toMatchItem);
    });
  }

  private _onWidgetChange(event: any): void {
    if (event.detail) {
      const detail = event.detail[0];
      const id = detail.el.id.split('__');
      const widget = this.widgets.find((w) => w.id === id[1]);
      widget.height = detail.h;
      widget.width = detail.w;
      widget.row = detail.y;
      widget.col = detail.x;

      if (!this._detectMob() && widget.widget !== 'heatmap_analytics') {
        this._opViewAPI
          .updateViewWidget(this.viewId, id[1], widget)
          .pipe(takeUntil(this._destroy$))
          .subscribe();
      }
      if (!this._detectMob() && widget.widget === 'heatmap_analytics') {
        this.analyticsWidget.destroyLayers();
        this._opViewAPI
          .updateViewWidget(this.viewId, id[1], widget)
          .pipe(takeUntil(this._destroy$))
          .subscribe((res) => {
            if (res) {
              this.analyticsWidget.rebuildLayers();
            }
          });
      }
    }
  }

  private _getWidgetPinnedState(): void {
    this._grid.getGridItems().forEach((item) => {
      const id = item.id.split('__');
      const widgetMatch = this.widgets.find((w) => w.id === id[1]);
      if (widgetMatch) {
        this._grid.update(item, {
          noMove: widgetMatch.isPinned,
          noResize: widgetMatch.isPinned,
          locked: widgetMatch.isPinned,
        });
      }
    });
  }
}
